import type {
	ButtonComponentColor,
	ButtonComponentSize,
} from '@sosocio/creator-types/app';
import cn from '@sosocio/ui/cn';

type ButtonState = 'default' | 'disabled' | 'focus' | 'hover' | 'pressed';
type ButtonStateClasses = Record<ButtonState, string>;

const BRAND_CLASSES: ButtonStateClasses = {
	default: 'border border-solid border-border-brand-primary bg-background-brand-primary text-text-default-secondary',
	disabled: 'border border-solid border-background-surface-disabled bg-background-surface-disabled text-text-default-disabled',
	focus: 'border-2 border-border-brand-primary-focus bg-background-brand-primary-focus shadow-[inset_0_0_0_1px_var(--color-border-neutral-border-1)] outline-none',
	hover: 'border-border-brand-primary-hover bg-background-brand-primary-hover',
	pressed: 'border border-border-brand-primary-pressed bg-background-brand-primary-pressed shadow-none',
};

const BRAND_INTERACTION_CLASSES = [
	'md:enabled:[&:hover:not(:focus-visible):not(:active)]:border-border-brand-primary-hover md:enabled:[&:hover:not(:focus-visible):not(:active)]:bg-background-brand-primary-hover',
	'enabled:[&:focus-visible:not(:active)]:border-2 enabled:[&:focus-visible:not(:active)]:border-border-brand-primary-focus enabled:[&:focus-visible:not(:active)]:bg-background-brand-primary-focus enabled:[&:focus-visible:not(:active)]:shadow-[inset_0_0_0_1px_var(--color-border-neutral-border-1)] enabled:[&:focus-visible:not(:active)]:outline-none',
	'enabled:active:border enabled:active:border-border-brand-primary-pressed enabled:active:bg-background-brand-primary-pressed enabled:active:shadow-none',
].join(' ');

const ICON_PRIMARY_CLASSES: Partial<ButtonStateClasses> = {
	default: 'text-icon-default-secondary',
	disabled: 'text-icon-default-disabled',
};

const ICON_SECONDARY_CLASSES: ButtonStateClasses = {
	default: [
		'text-icon-default-primary',
		'brandon:enabled:text-icon-brand-secondary',
		'safsira:enabled:text-icon-brand-primary',
	].join(' '),
	disabled: 'text-icon-default-disabled',
	focus: [
		'text-icon-brand-primary-focus',
		'agfa:enabled:text-icon-default-primary',
		'helloprint:enabled:text-icon-default-primary',
		'hema:enabled:text-icon-default-primary',
		'yoursurprise:enabled:text-icon-default-tertiary',
	].join(' '),
	hover: [
		'text-icon-brand-primary-hover',
		'agfa:enabled:text-icon-default-primary',
		'helloprint:enabled:text-icon-default-primary',
		'hema:enabled:text-icon-default-primary',
		'yoursurprise:enabled:text-icon-default-tertiary',
	].join(' '),
	pressed: [
		'text-icon-brand-primary-pressed',
		'agfa:enabled:text-icon-default-primary',
		'helloprint:enabled:text-icon-brand-primary',
		'hema:enabled:text-icon-default-primary',
		'yoursurprise:enabled:text-icon-default-tertiary',
	].join(' '),
};

const ICON_SECONDARY_INTERACTION_CLASSES = [
	'md:enabled:[&:hover:not(:focus-visible):not(:active)]:text-icon-brand-primary-hover',
	'agfa:md:enabled:[&:hover:not(:focus-visible):not(:active)]:text-icon-default-primary',
	'helloprint:md:enabled:[&:hover:not(:focus-visible):not(:active)]:text-icon-default-primary',
	'hema:md:enabled:[&:hover:not(:focus-visible):not(:active)]:text-icon-default-primary',
	'yoursurprise:md:enabled:[&:hover:not(:focus-visible):not(:active)]:text-icon-default-tertiary',
	'enabled:[&:focus-visible:not(:active)]:text-icon-brand-primary-focus',
	'agfa:enabled:[&:focus-visible:not(:active)]:text-icon-default-primary',
	'helloprint:enabled:[&:focus-visible:not(:active)]:text-icon-default-primary',
	'hema:enabled:[&:focus-visible:not(:active)]:text-icon-default-primary',
	'yoursurprise:enabled:[&:focus-visible:not(:active)]:text-icon-default-tertiary',
	'enabled:active:text-icon-brand-primary-pressed',
	'agfa:enabled:active:text-icon-default-primary',
	'helloprint:enabled:active:text-icon-brand-primary',
	'hema:enabled:active:text-icon-default-primary',
	'yoursurprise:enabled:active:text-icon-default-tertiary',
].join(' ');

const ICON_SIZE_CLASSES: Record<ButtonComponentSize, string> = {
	large: 'h-control-lg w-(--size-control-lg)',
	medium: 'h-control-md w-(--size-control-md)',
	small: 'h-control-sm w-(--size-control-sm)',
};

const LINK_CLASSES: ButtonStateClasses = {
	default: 'border-0 bg-transparent font-body text-text-default-primary no-underline decoration-[0.06em] underline-offset-[0.18em]',
	disabled: 'text-text-default-disabled',
	focus: 'border-0 text-text-brand-primary-focus shadow-[inset_0_0_0_1px_var(--color-border-brand-primary-focus)] outline-none',
	hover: 'text-text-brand-primary-hover',
	pressed: 'border-0 text-text-brand-primary-pressed shadow-none',
};

const LINK_INTERACTION_CLASSES = [
	'md:enabled:[&:hover:not(:focus-visible):not(:active)]:text-text-brand-primary-hover',
	'enabled:[&:focus-visible:not(:active)]:border-0 enabled:[&:focus-visible:not(:active)]:text-text-brand-primary-focus enabled:[&:focus-visible:not(:active)]:shadow-[inset_0_0_0_1px_var(--color-border-brand-primary-focus)] enabled:[&:focus-visible:not(:active)]:outline-none',
	'enabled:active:border-0 enabled:active:text-text-brand-primary-pressed enabled:active:shadow-none',
].join(' ');

/**
 * One unbroken line across the content box, including flex gaps and icons.
 * Inherited padding tracks caller overrides; the font-relative offset follows
 * the centered text rather than the button edge and clears brand-font descenders.
 * The line may extend into vertical padding when text is taller than the
 * control content box.
 */
const LINK_UNDERLINE_CLASSES = [
	'relative after:pointer-events-none after:absolute after:inset-0 after:content-[\'\']',
	'after:[padding:inherit] after:bg-origin-content after:bg-clip-padding after:bg-no-repeat',
	'after:bg-[linear-gradient(currentColor,currentColor)] after:bg-size-[100%_0.06em] after:bg-position-[center_calc(50%+0.7em)]',
].join(' ');

/** HEMA's approved purchase colors have no public semantic equivalent yet. */
const PURCHASE_CLASSES: ButtonStateClasses = {
	default: 'hema:border-(--primitive-color-accent-8a) hema:bg-(--primitive-color-accent-8a) hema:text-text-default-primary',
	disabled: 'hema:border-background-surface-disabled hema:bg-background-surface-disabled hema:text-text-default-disabled',
	focus: 'hema:border-(--primitive-color-accent-8b) hema:bg-(--primitive-color-accent-8b) hema:text-text-default-primary',
	hover: 'hema:border-(--primitive-color-accent-8b) hema:bg-(--primitive-color-accent-8b)',
	pressed: 'hema:border-(--primitive-color-accent-8b) hema:bg-(--primitive-color-accent-8b) hema:text-text-default-primary',
};

const PURCHASE_INTERACTION_CLASSES = [
	'hema:md:enabled:[&:hover:not(:focus-visible):not(:active)]:border-(--primitive-color-accent-8b) hema:md:enabled:[&:hover:not(:focus-visible):not(:active)]:bg-(--primitive-color-accent-8b)',
	'hema:enabled:[&:focus-visible:not(:active)]:border-(--primitive-color-accent-8b) hema:enabled:[&:focus-visible:not(:active)]:bg-(--primitive-color-accent-8b) hema:enabled:[&:focus-visible:not(:active)]:text-text-default-primary',
	'hema:enabled:active:border-(--primitive-color-accent-8b) hema:enabled:active:bg-(--primitive-color-accent-8b) hema:enabled:active:text-text-default-primary',
].join(' ');

/**
 * Figma secondary recipes differ by brand, even when their palettes expose the
 * same semantic roles. Keep defaults enabled-only so brand overrides cannot
 * win over the shared disabled colors. These classes also serve gallery states.
 * Brandon binds its foreground/border to secondary background state roles;
 * HelloPrint's unbound pressed foreground matches its existing primary role.
 */
const SECONDARY_CLASSES: ButtonStateClasses = {
	default: [
		'border border-solid border-border-neutral-border-5 bg-background-surface-fill-1 text-text-default-primary',
		'agfa:enabled:bg-background-brand-secondary',
		'brandon:enabled:border-background-brand-secondary brandon:enabled:text-text-brand-secondary',
		'helloprint:enabled:border-border-brand-primary helloprint:enabled:bg-background-brand-secondary',
		'safsira:enabled:border-border-brand-primary safsira:enabled:bg-background-brand-secondary safsira:enabled:text-text-brand-primary',
		'yoursurprise:enabled:border-border-neutral-border-2 yoursurprise:enabled:bg-background-brand-secondary',
	].join(' '),
	disabled: 'border-transparent bg-background-surface-disabled text-text-default-disabled',
	focus: [
		'border border-border-brand-primary-focus bg-background-brand-accent-fill-1 text-text-brand-primary-focus shadow-[inset_0_0_0_2px_var(--color-border-neutral-border-1)] outline-none',
		'agfa:enabled:border-border-neutral-border-5 agfa:enabled:bg-background-brand-secondary-focus agfa:enabled:text-text-default-primary agfa:enabled:shadow-[inset_0_0_0_2px_var(--color-background-surface-fill-3)]',
		'brandon:enabled:border-background-brand-secondary-focus brandon:enabled:bg-background-surface-fill-2 brandon:enabled:text-background-brand-secondary-focus',
		'helloprint:enabled:border-border-brand-primary-focus helloprint:enabled:bg-background-brand-secondary-focus helloprint:enabled:text-text-default-primary helloprint:enabled:shadow-[inset_0_0_0_2px_var(--color-background-surface-fill-4)]',
		'hema:enabled:border-border-neutral-border-5 hema:enabled:bg-background-surface-fill-2 hema:enabled:text-text-default-primary hema:enabled:shadow-[inset_0_0_0_2px_var(--color-background-surface-fill-4)]',
		'safsira:enabled:border-border-brand-primary-focus safsira:enabled:bg-background-brand-secondary-focus safsira:enabled:text-text-brand-primary-focus safsira:enabled:shadow-[inset_0_0_0_2px_var(--color-background-surface-fill-3)]',
		'yoursurprise:enabled:border-border-neutral-border-3 yoursurprise:enabled:bg-background-brand-secondary-focus yoursurprise:enabled:text-text-default-tertiary yoursurprise:enabled:shadow-[inset_0_0_0_2px_var(--color-background-surface-fill-4)]',
	].join(' '),
	hover: [
		'border-border-brand-primary-hover bg-background-brand-accent-fill-1 text-text-brand-primary-hover',
		'agfa:enabled:border-border-neutral-border-5 agfa:enabled:bg-background-brand-secondary-hover agfa:enabled:text-text-default-primary',
		'brandon:enabled:border-background-brand-secondary-hover brandon:enabled:bg-background-surface-fill-2 brandon:enabled:text-background-brand-secondary-hover',
		'helloprint:enabled:border-border-brand-primary-hover helloprint:enabled:bg-background-brand-secondary-hover helloprint:enabled:text-text-default-primary',
		'hema:enabled:border-border-neutral-border-5 hema:enabled:bg-background-surface-fill-2 hema:enabled:text-text-default-primary',
		'safsira:enabled:border-border-brand-primary-hover safsira:enabled:bg-background-brand-secondary-hover safsira:enabled:text-text-brand-primary-hover',
		'yoursurprise:enabled:border-border-neutral-border-3 yoursurprise:enabled:bg-background-brand-secondary-hover yoursurprise:enabled:text-text-default-tertiary',
	].join(' '),
	pressed: [
		'border border-border-brand-primary-pressed bg-background-brand-accent-fill-1 text-text-brand-primary-pressed shadow-none',
		'agfa:enabled:border-border-neutral-border-5 agfa:enabled:bg-background-brand-secondary-pressed agfa:enabled:text-text-default-primary',
		'brandon:enabled:border-background-brand-secondary-pressed brandon:enabled:bg-background-surface-fill-2 brandon:enabled:text-background-brand-secondary-pressed',
		'helloprint:enabled:border-border-brand-primary-pressed helloprint:enabled:bg-background-brand-secondary-pressed helloprint:enabled:text-text-brand-primary',
		'hema:enabled:border-border-neutral-border-5 hema:enabled:bg-background-surface-fill-2 hema:enabled:text-text-default-primary',
		'safsira:enabled:border-border-brand-primary-pressed safsira:enabled:bg-background-brand-secondary-pressed safsira:enabled:text-text-brand-primary-pressed',
		'yoursurprise:enabled:border-border-neutral-border-3 yoursurprise:enabled:bg-background-brand-secondary-focus yoursurprise:enabled:text-text-default-tertiary',
	].join(' '),
};

const SECONDARY_INTERACTION_CLASSES = [
	'md:enabled:[&:hover:not(:focus-visible):not(:active)]:border-border-brand-primary-hover md:enabled:[&:hover:not(:focus-visible):not(:active)]:bg-background-brand-accent-fill-1 md:enabled:[&:hover:not(:focus-visible):not(:active)]:text-text-brand-primary-hover',
	'agfa:md:enabled:[&:hover:not(:focus-visible):not(:active)]:border-border-neutral-border-5 agfa:md:enabled:[&:hover:not(:focus-visible):not(:active)]:bg-background-brand-secondary-hover agfa:md:enabled:[&:hover:not(:focus-visible):not(:active)]:text-text-default-primary',
	'brandon:md:enabled:[&:hover:not(:focus-visible):not(:active)]:border-background-brand-secondary-hover brandon:md:enabled:[&:hover:not(:focus-visible):not(:active)]:bg-background-surface-fill-2 brandon:md:enabled:[&:hover:not(:focus-visible):not(:active)]:text-background-brand-secondary-hover',
	'helloprint:md:enabled:[&:hover:not(:focus-visible):not(:active)]:border-border-brand-primary-hover helloprint:md:enabled:[&:hover:not(:focus-visible):not(:active)]:bg-background-brand-secondary-hover helloprint:md:enabled:[&:hover:not(:focus-visible):not(:active)]:text-text-default-primary',
	'hema:md:enabled:[&:hover:not(:focus-visible):not(:active)]:border-border-neutral-border-5 hema:md:enabled:[&:hover:not(:focus-visible):not(:active)]:bg-background-surface-fill-2 hema:md:enabled:[&:hover:not(:focus-visible):not(:active)]:text-text-default-primary',
	'safsira:md:enabled:[&:hover:not(:focus-visible):not(:active)]:border-border-brand-primary-hover safsira:md:enabled:[&:hover:not(:focus-visible):not(:active)]:bg-background-brand-secondary-hover safsira:md:enabled:[&:hover:not(:focus-visible):not(:active)]:text-text-brand-primary-hover',
	'yoursurprise:md:enabled:[&:hover:not(:focus-visible):not(:active)]:border-border-neutral-border-3 yoursurprise:md:enabled:[&:hover:not(:focus-visible):not(:active)]:bg-background-brand-secondary-hover yoursurprise:md:enabled:[&:hover:not(:focus-visible):not(:active)]:text-text-default-tertiary',
	'enabled:[&:focus-visible:not(:active)]:border enabled:[&:focus-visible:not(:active)]:border-border-brand-primary-focus enabled:[&:focus-visible:not(:active)]:bg-background-brand-accent-fill-1 enabled:[&:focus-visible:not(:active)]:text-text-brand-primary-focus enabled:[&:focus-visible:not(:active)]:shadow-[inset_0_0_0_2px_var(--color-border-neutral-border-1)] enabled:[&:focus-visible:not(:active)]:outline-none',
	'agfa:enabled:[&:focus-visible:not(:active)]:border-border-neutral-border-5 agfa:enabled:[&:focus-visible:not(:active)]:bg-background-brand-secondary-focus agfa:enabled:[&:focus-visible:not(:active)]:text-text-default-primary agfa:enabled:[&:focus-visible:not(:active)]:shadow-[inset_0_0_0_2px_var(--color-background-surface-fill-3)]',
	'brandon:enabled:[&:focus-visible:not(:active)]:border-background-brand-secondary-focus brandon:enabled:[&:focus-visible:not(:active)]:bg-background-surface-fill-2 brandon:enabled:[&:focus-visible:not(:active)]:text-background-brand-secondary-focus',
	'helloprint:enabled:[&:focus-visible:not(:active)]:border-border-brand-primary-focus helloprint:enabled:[&:focus-visible:not(:active)]:bg-background-brand-secondary-focus helloprint:enabled:[&:focus-visible:not(:active)]:text-text-default-primary helloprint:enabled:[&:focus-visible:not(:active)]:shadow-[inset_0_0_0_2px_var(--color-background-surface-fill-4)]',
	'hema:enabled:[&:focus-visible:not(:active)]:border-border-neutral-border-5 hema:enabled:[&:focus-visible:not(:active)]:bg-background-surface-fill-2 hema:enabled:[&:focus-visible:not(:active)]:text-text-default-primary hema:enabled:[&:focus-visible:not(:active)]:shadow-[inset_0_0_0_2px_var(--color-background-surface-fill-4)]',
	'safsira:enabled:[&:focus-visible:not(:active)]:border-border-brand-primary-focus safsira:enabled:[&:focus-visible:not(:active)]:bg-background-brand-secondary-focus safsira:enabled:[&:focus-visible:not(:active)]:text-text-brand-primary-focus safsira:enabled:[&:focus-visible:not(:active)]:shadow-[inset_0_0_0_2px_var(--color-background-surface-fill-3)]',
	'yoursurprise:enabled:[&:focus-visible:not(:active)]:border-border-neutral-border-3 yoursurprise:enabled:[&:focus-visible:not(:active)]:bg-background-brand-secondary-focus yoursurprise:enabled:[&:focus-visible:not(:active)]:text-text-default-tertiary yoursurprise:enabled:[&:focus-visible:not(:active)]:shadow-[inset_0_0_0_2px_var(--color-background-surface-fill-4)]',
	'enabled:active:border enabled:active:border-border-brand-primary-pressed enabled:active:bg-background-brand-accent-fill-1 enabled:active:text-text-brand-primary-pressed enabled:active:shadow-none',
	'agfa:enabled:active:border-border-neutral-border-5 agfa:enabled:active:bg-background-brand-secondary-pressed agfa:enabled:active:text-text-default-primary',
	'brandon:enabled:active:border-background-brand-secondary-pressed brandon:enabled:active:bg-background-surface-fill-2 brandon:enabled:active:text-background-brand-secondary-pressed',
	'helloprint:enabled:active:border-border-brand-primary-pressed helloprint:enabled:active:bg-background-brand-secondary-pressed helloprint:enabled:active:text-text-brand-primary',
	'hema:enabled:active:border-border-neutral-border-5 hema:enabled:active:bg-background-surface-fill-2 hema:enabled:active:text-text-default-primary',
	'safsira:enabled:active:border-border-brand-primary-pressed safsira:enabled:active:bg-background-brand-secondary-pressed safsira:enabled:active:text-text-brand-primary-pressed',
	'yoursurprise:enabled:active:border-border-neutral-border-3 yoursurprise:enabled:active:bg-background-brand-secondary-focus yoursurprise:enabled:active:text-text-default-tertiary',
].join(' ');

const TEXT_SIZE_CLASSES: Record<ButtonComponentSize, string> = {
	large: 'h-control-lg px-control py-control-lg',
	medium: 'h-control-md px-control py-control-md',
	small: 'h-control-sm px-control py-control-sm',
};

/**
 * Resolves variant-owned native hover, keyboard-focus, and active styles.
 *
 * @param color Finalized Button variant.
 * @returns Native pseudo-state Tailwind classes, including brand overrides.
 */
function getInteractionClasses(color: ButtonComponentColor): string {
	if (color === 'link' || color === 'link-underline') {
		return LINK_INTERACTION_CLASSES;
	}

	if (color === 'secondary' || color === 'icon-button-2') {
		return cn(
			SECONDARY_INTERACTION_CLASSES,
			color === 'icon-button-2' && ICON_SECONDARY_INTERACTION_CLASSES,
		);
	}

	return cn(
		BRAND_INTERACTION_CLASSES,
		color === 'purchase' && PURCHASE_INTERACTION_CLASSES,
	);
}

/**
 * Resolves one visual state without adding selectors or a component prop.
 *
 * @param color Finalized Button variant.
 * @param state Visual state to resolve.
 * @returns State-specific Tailwind classes, including brand overrides.
 */
function getStateClasses(
	color: ButtonComponentColor,
	state: ButtonState,
): string {
	if (color === 'link' || color === 'link-underline') {
		return LINK_CLASSES[state];
	}

	if (color === 'secondary' || color === 'icon-button-2') {
		return cn(
			SECONDARY_CLASSES[state],
			color === 'icon-button-2' && ICON_SECONDARY_CLASSES[state],
		);
	}

	return cn(
		BRAND_CLASSES[state],
		color === 'icon-button' && ICON_PRIMARY_CLASSES[state],
		color === 'purchase' && PURCHASE_CLASSES[state],
	);
}

/**
 * Builds Button geometry and enabled-state classes from its public variants.
 *
 * @param color Finalized Button variant.
 * @param size Token-based control size.
 * @param disabled Whether native interaction is disabled.
 * @param pressed Whether a production toggle stays visually engaged.
 * @returns Component classes ready for consumer classes to be merged last.
 */
export function getButtonClasses(
	color: ButtonComponentColor,
	size: ButtonComponentSize,
	disabled: boolean,
	pressed: boolean,
): string {
	const isIconButton = color === 'icon-button' || color === 'icon-button-2';

	return cn(
		'button-component flex items-center justify-center rounded-button text-transform-brand',
		getStateClasses(
			color,
			'default',
		),
		color === 'link-underline' && LINK_UNDERLINE_CLASSES,
		'gap-control',
		isIconButton ? [
			ICON_SIZE_CLASSES[size],
			'aspect-square p-0',
		] : TEXT_SIZE_CLASSES[size],
		color === 'primary' && 'hema:py-control-md',
		disabled
			? [
				'cursor-default',
				getStateClasses(
					color,
					'disabled',
				),
			]
			: [
				'cursor-pointer',
				'focus-visible:outline-none',
				(color === 'secondary' || color === 'icon-button-2') && [
					'hema:enabled:[&:focus-visible:active]:shadow-[inset_0_0_0_2px_var(--color-background-surface-fill-4)]',
					pressed && 'hema:enabled:focus-visible:shadow-[inset_0_0_0_2px_var(--color-background-surface-fill-4)]',
				],
				pressed ? getStateClasses(
					color,
					'pressed',
				) : getInteractionClasses(color),
			],
	);
}

/**
 * Shares the real Button palette with static hover/focus gallery specimens.
 * Production interaction remains owned by native pseudo-classes.
 *
 * @param color Finalized Button variant shown by the specimen.
 * @param state Hover or keyboard-focus state to preview.
 * @returns Tailwind classes to pass through the specimen's normal class attr.
 */
export function getButtonStateClasses(
	color: ButtonComponentColor,
	state: 'focus' | 'hover',
): string {
	return getStateClasses(
		color,
		state,
	);
}
